import type { ApiClient } from '../client';
import { CAREER_PATHS } from '../../../config/api-paths';
import type {
  CareerApplication,
  CareerJob,
  CareerPage,
  CareersConfig,
  PublicCareersMeta,
  UpsertCareerJob,
} from '../types/careers';

export function bindCareers(client: ApiClient) {
  return {
    async getPublicCareersMeta() {
      return client.request<PublicCareersMeta>(CAREER_PATHS.publicMeta, { skipAuth: true });
    },

    async listPublicCareerJobs(params?: Record<string, string | number | undefined>) {
      const query = new URLSearchParams();
      Object.entries(params ?? {}).forEach(([key, value]) => {
        if (value != null && String(value).trim()) query.set(key, String(value));
      });
      const suffix = query.toString() ? `?${query}` : '';
      return client.request<CareerPage<CareerJob>>(`${CAREER_PATHS.publicJobs}${suffix}`, {
        skipAuth: true,
      });
    },

    async applyForCareerJob(form: FormData) {
      const url = `${client.baseUrl}${CAREER_PATHS.publicApply}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: form,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as { error?: string }).error || response.statusText);
      }
      return { data: data as CareerApplication, status: response.status };
    },

    async getCareersConfig() {
      return client.request<CareersConfig>(CAREER_PATHS.adminConfig);
    },

    async saveCareersConfig(body: Partial<CareersConfig>) {
      return client.request<CareersConfig>(CAREER_PATHS.adminConfig, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async listAdminCareerJobs(params?: Record<string, string | number | undefined>) {
      const query = new URLSearchParams();
      Object.entries(params ?? {}).forEach(([key, value]) => {
        if (value != null && String(value).trim()) query.set(key, String(value));
      });
      const suffix = query.toString() ? `?${query}` : '';
      return client.request<CareerPage<CareerJob>>(`${CAREER_PATHS.adminJobs}${suffix}`);
    },

    async createCareerJob(body: UpsertCareerJob) {
      return client.request<CareerJob>(CAREER_PATHS.adminJobs, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async updateCareerJob(id: string, body: UpsertCareerJob) {
      return client.request<CareerJob>(CAREER_PATHS.adminJob(id), {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async listCareerApplications(params?: Record<string, string | number | undefined>) {
      const query = new URLSearchParams();
      Object.entries(params ?? {}).forEach(([key, value]) => {
        if (value != null && String(value).trim()) query.set(key, String(value));
      });
      const suffix = query.toString() ? `?${query}` : '';
      return client.request<CareerPage<CareerApplication>>(`${CAREER_PATHS.adminApplications}${suffix}`);
    },

    async updateCareerApplication(id: string, status: string) {
      return client.request<CareerApplication>(CAREER_PATHS.adminApplication(id), {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
    },

    async downloadCareerResume(id: string, filename: string) {
      const url = `${client.baseUrl}${CAREER_PATHS.adminResume(id)}`;
      const headers = client.authHeaders();
      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error('Could not download resume');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    },
  };
}
